import { get, keyBy, uniq } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import EventTimeline from 'dashboard/components/EventTimeline';
import { EventTimelinePage } from 'dashboard/components/EventTimeline/EventTimeline';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { Environment } from 'dashboard/models/environment';
import { Event, EventData } from 'dashboard/models/event';
import { Importer } from 'dashboard/models/importer';
import { ImporterRun } from 'dashboard/models/importer-run';
import { InquiryEvent } from 'dashboard/models/inquiry';
import { SUPPORTED_REPORT_TYPES } from 'dashboard/models/report';
import { User } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { showMessage } from 'dashboard/redux/actions/messages';
import { createFiltersForList } from 'dashboard/redux/thunk-actions/workflow-runs';
import useApiLoader from 'lib/hooks/useApiLoader';
import usePermission from 'lib/hooks/usePermission';
import { ApiEndpoint, ApiModel, ApiResource, PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

// N.B. This is not actually a redux connected component
// but it does its own fetches of data so its 'smart'
export interface ConnectedEventTimelineProps {
  objects: ApiResource[];
  currentEnvironment: Environment;
  location: EventTimelinePage;
}

const ConnectedEventTimeline = ({
  currentEnvironment,
  objects,
  location,
}: ConnectedEventTimelineProps) => {
  const [caseAssignmentPolicies, setCaseAssignmentPolicies] = useState<CaseAssignmentPolicy[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [workflowIdToNameMap, setWorkflowIdToNameMap] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<User[]>([]);
  const [importerPipelineMap, setImporterPipelineMap] = useState<
    Record<string, { id: string; name: string }>
  >({});
  const objectIds = objects.map((object) => object.id);
  const potentialTriggerObjects = objects.filter((object) =>
    [ApiModel.Inquiry, ApiModel.Case, ApiModel.Transaction, ...SUPPORTED_REPORT_TYPES].includes(
      object.type,
    ),
  );

  const hasWorkflowRunPermission = usePermission(Permission.WorkflowRunList);
  const hasAssignmentPolicyViewPermission = usePermission(Permission.CaseAssignmentPolicyView);
  const hasImporterPipelineViewPermission = usePermission(Permission.ImporterPipelineView);
  const dispatch = useDispatch();
  const abortController = useMemo(
    () => new AbortController(),
    [objectIds.toString(), currentEnvironment, setEvents, setWorkflowRuns, showMessage],
  );
  const signal = abortController.signal;

  const showMessageAction = (
    message: string,
    messageType?: MessageType,
    persist?: boolean,
    expiresAt?: string,
  ) => dispatch(showMessage(message, messageType, persist, expiresAt));

  const { loading } = useApiLoader({
    loader: async () => {
      const combinedWorkflowIdToNameMap: Record<string, string> = {};
      let createdWorkflowRuns: WorkflowRun[] = [];
      if (hasWorkflowRunPermission) {
        const createdWorkflowRunResponses = await Promise.all(
          potentialTriggerObjects.map((object) =>
            SessionClientDashboard.listResource<WorkflowRun, PaginationParams>(
              ApiEndpoint.WorkflowRun,
              {
                filter: createFiltersForList(currentEnvironment, {
                  triggerObjectId: object.id,
                  status: [],
                  workflowIds: [],
                  workflowVersionIds: [],
                }),
                page: {
                  size: 100,
                },
                signal,
              },
            ),
          ),
        );
        createdWorkflowRuns = createdWorkflowRunResponses.flatMap((response) => {
          (response.included || [])
            .filter((item): item is Workflow => item.type === ApiModel.Workflow)
            .forEach((workflow: Workflow) => {
              combinedWorkflowIdToNameMap[workflow.id] = workflow.attributes.name;
            });
          return response.data;
        });
      }

      // TODO: Should loop and grab all events. Currently this is brittle.
      // TODO: this causes an unhandledpromiserejection error for txns
      const { data: events } = await SessionClientDashboard.listResource<Event, PaginationParams>(
        ApiEndpoint.Event,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'object-id': [...objectIds, ...createdWorkflowRuns.map((run) => run.id)],
          },
          page: {
            size: 100,
          },
          signal,
        },
      );

      /* Some events may not have payloads if the current user doesn't have permission
       * to see those payloads.
       */
      const permissibleEvents = events.filter((event) =>
        Object.prototype.hasOwnProperty.call(event.attributes, 'payload'),
      );

      /* We can't look at included because creators and reviewers are not
       * included because they are not a formal association on inquiries/cases
       *
       * V_2021_07_05 does a replacement of the reviewer field.
       * Instead of being placed under `attributes` its placed under `relationships`.
       * We need to look at both for backwards compatibility.
       */
      const workflowRunInquiryReviewersFromAttributes = permissibleEvents
        .map((event) => event.attributes.payload.data)
        .filter(
          (eventPayload: EventData): eventPayload is InquiryEvent =>
            eventPayload.type === ApiModel.Inquiry,
        )
        .filter((inquiryEvent) => inquiryEvent.attributes['reviewer-type'] === ApiModel.WorkflowRun)
        .map((inquiryEvent) => inquiryEvent.attributes.reviewer);

      const workflowRunInquiryReviewersFromRelationships = permissibleEvents
        .map((event) => event.attributes.payload.data)
        .filter(
          (eventPayload: EventData): eventPayload is InquiryEvent =>
            eventPayload.type === ApiModel.Inquiry,
        )
        .filter(
          (inquiryEvent) =>
            inquiryEvent.relationships.reviewer?.data?.type === ApiModel.WorkflowRun,
        )
        .map((inquiryEvent) => inquiryEvent.relationships.reviewer.data.id);

      const workflowRunCaseCreators = getActors({
        events: permissibleEvents,
        eventTypes: [ApiModel.Case],
        actorType: ApiModel.WorkflowRun,
        actorTypePath: 'attributes.payload.data.attributes.creator-type',
        actorIdPath: 'attributes.payload.data.attributes.creator-id',
      });

      const workflowRunCaseResolvers = getActors({
        events: permissibleEvents,
        eventTypes: [ApiModel.Case],
        actorType: ApiModel.WorkflowRun,
        actorTypePath: 'attributes.payload.data.attributes.resolver-type',
        actorIdPath: 'attributes.payload.data.attributes.resolver-id',
      });

      const workflowRunCaseAndAccountUpdaters = getActors({
        events: permissibleEvents,
        eventTypes: [ApiModel.Case, ApiModel.Account],
        actorType: ApiModel.WorkflowRun,
        actorTypePath: 'attributes.payload.data.attributes.updater-type',
        actorIdPath: 'attributes.payload.data.attributes.updater-id',
      });

      const workflowRunUpdatersFromContext = getActors({
        events: permissibleEvents,
        actorType: ApiModel.WorkflowRun,
        actorTypePath: 'attributes.context.updater-type',
        actorIdPath: 'attributes.context.updater-id',
      });

      const allRuns = uniq([
        ...workflowRunInquiryReviewersFromAttributes,
        ...workflowRunInquiryReviewersFromRelationships,
        ...workflowRunCaseCreators,
        ...workflowRunCaseResolvers,
        ...workflowRunCaseAndAccountUpdaters,
        ...workflowRunUpdatersFromContext,
      ]);
      let workflowRuns: WorkflowRun[] = [];

      if (hasWorkflowRunPermission && allRuns.length) {
        const { data, included } = await SessionClientDashboard.listResource<
          WorkflowRun,
          PaginationParams
        >(ApiEndpoint.WorkflowRun, {
          filter: {
            'environment-id': currentEnvironment.id,
            'workflow-run-ids': allRuns,
          },
          page: {
            size: 100,
          },
          signal,
        });
        (included || [])
          .filter((item): item is Workflow => item.type === ApiModel.Workflow)
          .forEach((workflow: Workflow) => {
            combinedWorkflowIdToNameMap[workflow.id] = workflow.attributes.name;
          });

        const { data: directRunsData, included: directRunsIncluded } =
          await SessionClientDashboard.listResource<WorkflowRun, PaginationParams>(
            ApiEndpoint.WorkflowRun,
            {
              filter: {
                'environment-id': currentEnvironment.id,
                'workflow-run-ids': allRuns,
                'trigger-type': ApiModel.TriggerDirect,
              },
              page: {
                size: 100,
              },
              signal,
            },
          );
        (directRunsIncluded || [])
          .filter((item): item is Workflow => item.type === ApiModel.Workflow)
          .forEach((workflow: Workflow) => {
            combinedWorkflowIdToNameMap[workflow.id] = workflow.attributes.name;
          });

        workflowRuns = [...data, ...directRunsData];
      }

      const { data: users } = await SessionClientDashboard.listResource<User>(ApiEndpoint.User, {
        signal,
      });

      let assignmentPolicies: CaseAssignmentPolicy[] = [];
      if (hasAssignmentPolicyViewPermission) {
        ({ data: assignmentPolicies } =
          await SessionClientDashboard.listResource<CaseAssignmentPolicy>(
            ApiEndpoint.CaseAssignmentPolicy,
            {
              signal,
            },
          ));
      }

      const importerUpdatersFromContext = getActors({
        events: permissibleEvents,
        actorType: ApiModel.ImporterRun,
        actorTypePath: 'attributes.context.updater-type',
        actorIdPath: 'attributes.context.updater-id',
      });
      let importerPipelineMap: Record<string, { id: string; name: string }> = {};
      if (hasImporterPipelineViewPermission && importerUpdatersFromContext.length) {
        const { data: importerPipelineRuns, included: importerPipelineRunsIncluded } =
          await SessionClientDashboard.listResource<ImporterRun>(ApiEndpoint.ImporterRun, {
            filter: {
              'environment-id': currentEnvironment.id,
              'importer-pipeline-run-ids': importerUpdatersFromContext,
            },
            page: {
              size: 100,
            },
            signal,
          });

        const includedItemsMap = keyBy('id', importerPipelineRunsIncluded);

        importerPipelineMap = importerPipelineRuns.reduce(
          (acc: Record<string, { id: string; name: string }>, run) => {
            const importerPipelineId = run.relationships['importer-pipeline'].data.id;
            const importerPipeline = includedItemsMap[importerPipelineId] as Importer;
            acc[run.id] = {
              id: importerPipelineId,
              name: importerPipeline?.attributes?.name,
            };
            return acc;
          },
          {},
        );
      }

      setWorkflowIdToNameMap(combinedWorkflowIdToNameMap);
      setEvents(permissibleEvents);
      setWorkflowRuns([...createdWorkflowRuns, ...workflowRuns]);
      setUsers(users);
      setCaseAssignmentPolicies(assignmentPolicies);
      setImporterPipelineMap(importerPipelineMap);
    },
    deps: [objectIds.toString(), currentEnvironment, setEvents, setWorkflowRuns, showMessage],
    abortController: abortController,
    onError: () => showMessageAction('Failed to load events.', MessageType.Failure),
    onNetworkError: () =>
      showMessageAction(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });
  if (loading) {
    return <LoadingIndicator />;
  }
  return (
    <EventTimeline
      data-test='connected-event-timeline'
      caseAssignmentPolicies={caseAssignmentPolicies}
      importerPipelineMap={importerPipelineMap}
      events={events}
      workflowIdToNameMap={workflowIdToNameMap}
      workflowRuns={workflowRuns}
      users={users}
      location={location}
    />
  );
};

const getActors = ({
  events,
  eventTypes,
  actorType,
  actorTypePath,
  actorIdPath,
}: {
  events: Event[];
  eventTypes?: ApiModel[];
  actorType: ApiModel;
  actorTypePath: string;
  actorIdPath: string;
}) => {
  return events
    .filter((event) =>
      eventTypes ? eventTypes.includes(event.attributes.payload.data.type) : true,
    )
    .filter((event) => get(actorTypePath, event) === actorType)
    .map((event) => get(actorIdPath, event))
    .filter((actorId) => !(actorId == null));
};

export default ConnectedEventTimeline;
